import { AfterViewInit, Component, Inject, OnDestroy, OnInit, TemplateRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { SettingsService } from '@app/services/settings.service';
import { finalize, take, tap, takeUntil } from 'rxjs/operators';
import { SettingsProfileFieldType, PlatformFeaturesKey } from 'library-explorer';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UserCertificate } from '@app/model/user-certificate.model';
import { Achievement } from '@app/model/achievement';
import { LeaderboardNotification } from '@app/model/leaderboard-notification';
import { LessonService } from '@app/services/api/lesson.service';
import { Observable, of, Subject } from 'rxjs';
import { CertificateService } from '@app/services/certificate.service';
import { NavigationItemsPipe } from '@app/shared/pipes/navigation-items.pipe';
import { RewardNotificationService } from '@app/services/reward-notification.service';

@Component({
    selector: 'app-reward-modal',
    templateUrl: './reward-modal.component.html',
    styleUrls: ['./reward-modal.component.scss'],
    providers: [NavigationItemsPipe],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class RewardModalComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('feedbackPageTemplate') feedbackPageTemplate: TemplateRef<any>;
  @ViewChild('pointsPageTemplate') pointsPageTemplate: TemplateRef<any>;
  @ViewChild('badgePageTemplate') badgePageTemplate: TemplateRef<any>;
  @ViewChild('certificatePageTemplate') certificatePageTemplate: TemplateRef<any>;

  public currentPageIndex = 0;
  public ratings = new Array(5).fill(0).map((_, index) => index + 1);
  public feedbackForm: FormGroup;
  public settings: any;
  public feedbackSettings: any = {};
  public formVisible = false;
  public isContinueLoading = false;
  public certificateLoading = false;
  public pages: {type: string, template: TemplateRef<any>, data: any}[] = [];
  public isBadgesVisible = false;

  public readonly fieldTypes: typeof SettingsProfileFieldType = SettingsProfileFieldType;
  public readonly platformFeaturesKey: typeof PlatformFeaturesKey = PlatformFeaturesKey;
  
  private unsubscribe: Subject<void> = new Subject();

  constructor(
    @Inject(MAT_DIALOG_DATA) private dialogData: {certificates: UserCertificate[], achievements: Achievement[], points: LeaderboardNotification[], showFeedback?: boolean, lessonId?: string},
    private readonly dialogRef: MatDialogRef<RewardModalComponent>,
    private readonly formBuilder: FormBuilder,
    private readonly settingsService: SettingsService,
    private readonly lessonService: LessonService,
    private readonly certificateService: CertificateService,
    private readonly navigationItemsPipe: NavigationItemsPipe,
    private readonly rewardNotificationService: RewardNotificationService,
  ) { }

  ngOnInit(): void {
    this.initFeedbackForm();
    this.initSettings();
  }

  ngAfterViewInit(): void {
    this.initData();
  }

  public selectPage(index: number): void {
    this.currentPageIndex = index;
  }

  public nextPage(): void {
    const nextLogic = () => {
      if (this.currentPageIndex >= this.pages.length - 1) {
        this.dialogRef.close();
        return;
      }
  
      this.currentPageIndex++;
    }


    if (this.pages[this.currentPageIndex].type === 'feedback') {
      this.sendFeedback()
        .pipe(
          finalize(() => nextLogic()),
          tap((res: any) => {
            if (!res) {
              return;
            }

            this.rewardNotificationService.checkRewards();
          })
        )
        .subscribe();
        return;
    }

    nextLogic();
  }

  public setRating(rate: number): void {
    this.feedbackForm.patchValue({
      rate
    });
  }

  public showForm(): void {
    this.formVisible = true;
  }

  public downloadCertificate(certificate: UserCertificate): void {
    certificate = {...certificate, customCertificate: certificate.certifications[0]};
    this.certificateLoading = true;
    this.certificateService.downloadCerfiticate(certificate)
    .pipe(
      finalize(() => this.certificateLoading = null)
    )
    .subscribe(() => {});
  }

  public navigateInNewTab(path: string): void {
    window.open(path, '_blank');
  }

  private initFeedbackForm(): void {
    this.feedbackForm = this.formBuilder.group({
      rate: [null],
    });
  }

  private initSettings(): void {
    this.settingsService.getSettings().pipe(
      take(1)
    ).subscribe(settings => {
      this.settings = settings;
      this.feedbackSettings = settings.feedback || {};
      this.formVisible = !this.feedbackSettings?.showFormButtonEnabled
      this.addFieldsToForm();
      
      this.navigationItemsPipe.breakpoint$.pipe(
        takeUntil(this.unsubscribe)
      ).subscribe(breakpoint => {
        this.isBadgesVisible = Object.keys(settings.menu).some(key => settings.menu[key]?.achievements?.[breakpoint]?.visible);
      })
    })
  }

  private addFieldsToForm(): void {
    if (!this.feedbackSettings?.fields?.length) {
      return;
    }

    this.feedbackSettings.fields.forEach(field => {
      const control = this.formBuilder.control('');
      this.feedbackForm.addControl(field.id, control);
    });
  }

  private initData(): void {
    const points = this.dialogData.points.reduce((prev, item) => prev + (+item.earnedPoints), 0);
    const currentPosition = this.dialogData.points.reduce((prev, item) => {prev = item.currentPosition < prev ? item.currentPosition : prev; return prev}, Infinity);

    this.pages = [];

    if (this.dialogData.showFeedback) {
      this.pages.push({
        type: 'feedback',
        template: this.feedbackPageTemplate,
        data: {}
      })
    }
    
    if (points) {
      this.pages.push({
        type: 'points',
        template: this.pointsPageTemplate,
        data: {
          points,
          currentPosition
        }
      })
    }

    this.dialogData.achievements.forEach(achievement => {
      this.pages.push({
        type: 'badge',
        template: this.badgePageTemplate,
        data: achievement
      });
    });

    this.dialogData.certificates.forEach(cert => {
      this.pages.push({
        type: 'certificate',
        template: this.certificatePageTemplate,
        data: cert
      })
    });
  }

  private sendFeedback(): Observable<any> {
    if (this.feedbackForm.invalid) {
      return of(false);
    }

    const { rate, ...fieldsObject } = this.feedbackForm.value;

    const fields = Object.keys(fieldsObject)?.reduce((prev, key) => {
      prev = [...prev, { id: key, value: fieldsObject[key] }];
      return prev;
    }, []);


    const formDataFilled = fields.some(item => !!item?.value) || rate;

    if (!formDataFilled) {
      return of(false);
    }

    this.isContinueLoading = true;

    return this.lessonService.sendFeedback(this.dialogData.lessonId, {rate, fields}).pipe(
      finalize(() => {
        this.isContinueLoading = false;
      })
    )
  }
  
  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
